"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Mail, MapPin, Phone, User2 } from "lucide-react"
import { getInTouchSchema, type GetInTouchSchema } from "@/lib/validation"
import { useTransition } from "react"
import { toast } from "sonner"
import LoadingButton from "../ui/loading-button"
import { getInTouchFormAction } from "@/server/actions/google-sheets-actions"

export default function GetInTouchForm({ destination, note, onSuccess }: { destination?: string, note?: boolean, onSuccess?: () => void }) {
    const [isPending, startTransition] = useTransition()

    const form = useForm<GetInTouchSchema>({
        resolver: zodResolver(getInTouchSchema),
        defaultValues: {
            name: "",
            email: "",
            phone: "",
            destination: destination,
        },
    })

    function onSubmit(data: GetInTouchSchema) {
        startTransition(async () => {
            const res = await getInTouchFormAction(data);
            if (!res.success) {
                toast.error(res.message)
                return;
            }

            toast.success(res.message)
            form.reset()
            onSuccess?.()
        })
    }

    return (
        <div className="p-4 sm:p-5 my-2.5 sm:my-4 rounded-lg shadow-[0px_4px_16px_4px_rgba(0,0,0,0.1)] md:p-0 md:shadow-none">
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="w-full max-w-md space-y-4">
                    <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <div className="relative">
                                        <Input placeholder="Enter Your Name" {...field} className="h-12 peer placeholder:text-secondary-text/55 text-black" />
                                        <User2 className="h-5 w-5 absolute top-1/2 right-2.5 -translate-y-1/2 text-secondary-text/55 peer-focus-visible:text-custom-primary" strokeWidth={2} />
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <div className="relative">
                                        <Input placeholder="Enter Your Email" {...field} className="h-12 peer placeholder:text-secondary-text/55 text-black" />
                                        <Mail className="h-5 w-5 absolute top-1/2 right-2.5 -translate-y-1/2 text-secondary-text/55 peer-focus-visible:text-custom-primary" strokeWidth={2} />
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <div className="relative">
                                        <Input placeholder="Enter Phone Number" {...field} className="h-12 peer placeholder:text-secondary-text/55 text-black" />
                                        <Phone className="h-5 w-5 absolute top-1/2 right-2.5 -translate-y-1/2 text-secondary-text/55 peer-focus-visible:text-custom-primary" strokeWidth={2} />
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="destination"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <div className="relative">
                                        <Input placeholder="Enter Your Destination" {...field} className="h-12 peer placeholder:text-secondary-text/55 text-black" />
                                        <MapPin className="h-5 w-5 absolute top-1/2 right-2.5 -translate-y-1/2 text-secondary-text/55 peer-focus-visible:text-custom-primary" strokeWidth={2} />
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <LoadingButton loading={isPending} type="submit" className="primary-btn bg-custom-primary w-full text-base">
                        Submit Now
                    </LoadingButton>
                </form>
            </Form>
            {note && <p className="font-medium text-red-600 font-lexend text-sm text-center mt-3">
                **No spam, no unwanted calls, only personalised trip planning.
            </p>}
        </div>
    )
}
